(() => {
  const anywhereColourways = [
    'Washed Black Distressed',
    'Khaki Green & White',
    'Black & White',
    'Cream & Red',
    'Grey & White',
    'Grey & Yellow',
    'White & Black',
    'White & Blue',
    'Tan & White',
    'White Distressed',
    'Concrete Grey',
    'Vintage Khaki',
    'Melange Grey',
    'Indigo Blue',
    'Khaki Green',
    'Washed Black',
    'Wavy Navy',
    'Jet Black',
    'Grey Black'
  ].sort((a, b) => b.length - a.length);

  const edgeColourways = [
    'Sandwash',
    'Charcoal',
    'Chocolate',
    'Burgundy',
    'Cream',
    'Brown',
    'Silver',
    'Yellow',
    'Purple',
    'Orange',
    'Green',
    'Grey',
    'Gray',
    'Black',
    'White',
    'Navy',
    'Khaki',
    'Indigo',
    'Cobalt',
    'Beige',
    'Blue',
    'Pink',
    'Red',
    'Tan',
    'Sand',
    'Olive',
    'Stone'
  ].sort((a, b) => b.length - a.length);

  const isBoundary = (character) => !character || /[\s\-–—/]/.test(character);

  const findPhrase = (title, phrase) => {
    const lowerTitle = title.toLocaleLowerCase();
    const lowerPhrase = phrase.toLocaleLowerCase();
    let searchFrom = 0;

    while (searchFrom < lowerTitle.length) {
      const index = lowerTitle.indexOf(lowerPhrase, searchFrom);
      if (index === -1) return null;

      const end = index + phrase.length;
      const before = index === 0 ? '' : title.charAt(index - 1);
      const after = end >= title.length ? '' : title.charAt(end);

      if (isBoundary(before) && isBoundary(after)) {
        return {
          start: index,
          end,
          value: title.slice(index, end)
        };
      }

      searchFrom = index + 1;
    }

    return null;
  };

  const trimEdgeDelimiters = (value) => value.replace(/^[\s\-–—/]+|[\s\-–—/]+$/g, '').trim();

  const cleanProductName = (value) =>
    trimEdgeDelimiters(value)
      .replace(/\s{2,}/g, ' ')
      .trim();

  const splitTitle = (title) => {
    for (const colourway of anywhereColourways) {
      const match = findPhrase(title, colourway);
      if (!match) continue;

      const name = cleanProductName(`${title.slice(0, match.start)} ${title.slice(match.end)}`);
      if (name) return { name, colourway: match.value };
    }

    for (const colourway of edgeColourways) {
      const match = findPhrase(title, colourway);
      if (!match) continue;

      const before = trimEdgeDelimiters(title.slice(0, match.start));
      const after = trimEdgeDelimiters(title.slice(match.end));
      if (before && after) continue;

      const name = cleanProductName(`${title.slice(0, match.start)} ${title.slice(match.end)}`);
      if (name) return { name, colourway: match.value };
    }

    return { name: title, colourway: '' };
  };

  const applyCardTitleHover = (card, active) => {
    const titleElement = card.querySelector('.product-card__title');
    const titleLink = titleElement?.querySelector('a');

    [titleElement, titleLink].filter(Boolean).forEach((element) => {
      if (active) {
        element.style.setProperty('font-weight', '700', 'important');
        element.style.setProperty('font-variation-settings', '"wght" 700', 'important');
        element.style.setProperty('font-synthesis', 'weight', 'important');
        element.style.setProperty('-webkit-text-stroke', '0', 'important');
      } else {
        element.style.removeProperty('font-weight');
        element.style.removeProperty('font-variation-settings');
        element.style.removeProperty('font-synthesis');
        element.style.removeProperty('-webkit-text-stroke');
      }
    });
  };

  const bindCardTitleHover = (card) => {
    if (card.dataset.jeaniusTitleHoverBound === 'true') return;
    card.dataset.jeaniusTitleHoverBound = 'true';

    card.addEventListener('mouseenter', () => applyCardTitleHover(card, true));
    card.addEventListener('mouseleave', () => applyCardTitleHover(card, false));
    card.addEventListener('focusin', () => applyCardTitleHover(card, true));
    card.addEventListener('focusout', (event) => {
      if (!card.contains(event.relatedTarget)) applyCardTitleHover(card, false);
    });
  };

  const formatCard = (card) => {
    const titleElement = card.querySelector('.product-card__title');
    const titleLink = titleElement?.querySelector('a');
    if (!titleElement || !titleLink) return;

    const sourceTitle = titleLink.dataset.fullProductTitle || titleLink.textContent.trim();
    if (!sourceTitle) return;

    titleLink.dataset.fullProductTitle = sourceTitle;
    titleLink.setAttribute('aria-label', sourceTitle);

    const { name, colourway } = splitTitle(sourceTitle);
    if (titleLink.textContent.trim() !== name) titleLink.textContent = name;

    let colourwayLine = titleElement.nextElementSibling;
    if (!colourwayLine?.classList.contains('product-card__colourway')) {
      colourwayLine = document.createElement('div');
      colourwayLine.className = 'product-card__colourway';
      colourwayLine.setAttribute('aria-hidden', 'true');
      titleElement.insertAdjacentElement('afterend', colourwayLine);
    }

    colourwayLine.textContent = colourway || '\u00a0';
    bindCardTitleHover(card);
  };

  const formatProductPage = (productSection) => {
    const titleElement = productSection.querySelector('.product__info-container .product__title');
    if (!titleElement) return;

    const titleTextElement = titleElement.querySelector('a') || titleElement;
    const sourceTitle = titleTextElement.dataset.fullProductTitle || titleTextElement.textContent.trim();
    if (!sourceTitle) return;

    titleTextElement.dataset.fullProductTitle = sourceTitle;
    titleTextElement.setAttribute('aria-label', sourceTitle);

    const { name, colourway } = splitTitle(sourceTitle);
    if (titleTextElement.textContent.trim() !== name) titleTextElement.textContent = name;

    let colourwayLine = titleElement.nextElementSibling;
    if (!colourwayLine?.classList.contains('product__colourway')) {
      colourwayLine = document.createElement('div');
      colourwayLine.className = 'product__colourway';
      colourwayLine.setAttribute('aria-hidden', 'true');
      titleElement.insertAdjacentElement('afterend', colourwayLine);
    }

    colourwayLine.textContent = colourway || '\u00a0';
  };

  const formatAllProductText = (root = document) => {
    if (root instanceof Element && root.matches('.product-card')) formatCard(root);
    root.querySelectorAll?.('.product-card').forEach(formatCard);

    if (root instanceof Element && root.matches('.product-section')) formatProductPage(root);
    root.querySelectorAll?.('.product-section').forEach(formatProductPage);
  };

  const start = () => {
    formatAllProductText();

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (!(node instanceof Element)) return;
          formatAllProductText(node);
        });
      });
    });

    observer.observe(document.body, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
